import { useMutation } from '@tanstack/react-query';
import * as Yup from 'yup';
import { updateSiteRecce } from '@api/services/site-recce.service';
import { ISiteReccee, ISiteRecceeRemarks } from '@api/models/site-recce.model';
import { AssetTypeEnum, SiteReccePriorityEnum, SiteRecceStatusEnum, SiteRecceTypeEnum } from '@api/enum';
import { IClientInstallation, IProjectInstallation } from '@api/models';

export type TSiteRecceUpdateFormValues = {
    preferredDate: string;
    priority: SiteReccePriorityEnum;
    type: SiteRecceTypeEnum | null;
    assetType: AssetTypeEnum | null;
    poRecieved: boolean;
    quantity?: number;
    assignedTo?: string;
    assignedDate?: string;
    status: SiteRecceStatusEnum;
    clientInstallation?: IClientInstallation;
    projectInstallation?: IProjectInstallation;
    // rejectionRemarks?: string;
    // completionRemarks?: string;
    // remarks?: string;
};

const useSiteRecceOperationUpdateForm = (
    clientInstallationId: string,
    projectInstallationId: string,
    initialData: ISiteReccee
) => {
    const schema = Yup.object({
        remarks: Yup.string(),
        preferredDate: Yup.string().nullable(),
        priority: Yup.mixed<SiteReccePriorityEnum>()
            .oneOf(Object.values(SiteReccePriorityEnum))
            .required('Priority is required'),
        type: Yup.mixed<SiteRecceTypeEnum>()
            .oneOf([...Object.values(SiteRecceTypeEnum), null])
            .nullable(),
        assetType: Yup.mixed<AssetTypeEnum>()
            .oneOf([...Object.values(AssetTypeEnum), null])
            .nullable(),
        poRecieved: Yup.boolean().required('PO status is required'),

        assignedDate: Yup.string().nullable(),
        status: Yup.string().required('Status is required'),
        assignedTo: Yup.string().when('status', {
            is: SiteRecceStatusEnum.ASSIGNED,
            then: (schema) => schema.required('Assigned user is required'),
            otherwise: (schema) => schema.notRequired(),
        }),
    });

    const initialValues: TSiteRecceUpdateFormValues = {
        preferredDate: initialData?.preferredDate || '',
        priority: initialData?.priority || SiteReccePriorityEnum.HIGH,
        type: initialData?.type ?? null,
        assetType: initialData?.assetType ?? null,
        poRecieved: initialData?.poRecieved ?? false,
        quantity: initialData?.quantity ?? 0,
        assignedTo: typeof initialData?.assignedTo === 'string' ? initialData.assignedTo : initialData.assignedTo?._id,
        assignedDate: initialData?.assignedDate || '',
        status: initialData?.status || SiteRecceStatusEnum.UNASSIGNED,
        clientInstallation: initialData.clientInstallation,
        projectInstallation: initialData.projectInstallation,
        // rejectionRemarks: initialData.rejectionRemarks,
        // completionRemarks: initialData.completionRemarks,
        // remarks: initialData.remarks || [],
        // remarks: '',
    };

    // Main update mutation (e.g. form submission)
    const submitMutation = useMutation((payload: TSiteRecceUpdateFormValues) =>
        updateSiteRecce(clientInstallationId, projectInstallationId, initialData._id, payload)
    );

    // Separate cancel mutation
    // const cancelMutation = useMutation((reason?: string) => {
    //     const cancelPayload: TSiteRecceUpdateFormValues = {
    //         ...initialValues,
    //         status: SiteRecceStatusEnum.REJECTED,
    //         remarks: reason ?? '',
    //     };
    //     return updateSiteRecce(clientInstallationId, projectInstallationId, initialData._id, cancelPayload);
    // });

    // Separate complete mutation
    // const completeMutation = useMutation((remarks?: string) => {
    //     const completePayload: TSiteRecceUpdateFormValues = {
    //         ...initialValues,
    //         status: SiteRecceStatusEnum.COMPLETED,
    //         completionRemarks: remarks ?? '',
    //     };
    //     return updateSiteRecce(clientInstallationId, projectInstallationId, initialData._id, completePayload);
    // });

    const submit = async (values: TSiteRecceUpdateFormValues) => {
        const data = await submitMutation.mutateAsync(values);
        return data;
    };

    // const cancelRecce = async (reason?: string) => {
    //     const data = await cancelMutation.mutateAsync(reason);
    //     return data;
    // };

    // const completeRecce = async (completionRemarks?: string) => {
    //     const data = await completeMutation.mutateAsync(completionRemarks);
    //     return data;
    // };

    return {
        initialValues,
        schema,
        isSubmitting: submitMutation.isLoading,
        // isCancelling: cancelMutation.isLoading, // ✅ Separate loading state
        // isCompleting: completeMutation.isLoading, // ✅ Separate loading state
        submit,
        // cancelRecce,
        // completeRecce,
    };
};

export default useSiteRecceOperationUpdateForm;
